<template>
  <v-content class="gb">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12">
          <v-card
            :loading="status === 'loading'"
            class="fill-width table_theme card-theme custom-container"
          >
            <v-card-title>
              <span class="title_table medium-font" style="letter-spacing: normal">
                Referrals
              </span>
              <v-spacer></v-spacer>
              <v-btn rounded small dark color="#061D47"
                  style="width: 120px !important; margin-right: 8px; text-transform: none; font-weight: 600;"
                  @click="$router.push('/owner/vendors')"
              >Vendors</v-btn>
              <v-btn rounded small dark color="#5C6BC0"
                  style="width: 120px !important; margin-right: 8px; text-transform: none; font-weight: 600;"
                  @click="$router.push('/owner/vendor-coverage')"
              >Coverage</v-btn>
              <v-btn-toggle v-model="statusFilter" mandatory dense class="mr-4">
                <v-btn small value="all">All</v-btn>
                <v-btn small value="pending">Pending</v-btn>
                <v-btn small value="approved">Approved</v-btn>
                <v-btn small value="declined">Declined</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-divider style="max-width: 1096px;"></v-divider>

            <v-card-text>
              <!-- Empty state -->
              <div v-if="!loading && recommendations.length === 0" class="text-center py-8">
                <p style="color: #888; font-size: 14px;">
                  <template v-if="statusFilter === 'pending'">
                    No pending vendor recommendations.
                  </template>
                  <template v-else>
                    No {{ statusFilter }} recommendations found.
                  </template>
                </p>
              </div>

              <!-- Recommendation cards -->
              <div v-else class="rec-cards-grid">
                <v-card
                  v-for="rec in recommendations"
                  :key="rec.id"
                  outlined
                  class="rec-card"
                  :class="{ 'rec-card--approved': rec.status === 'approved', 'rec-card--declined': rec.status === 'declined' }"
                >
                  <v-card-text class="pb-2">
                    <div class="d-flex align-center mb-2">
                      <v-avatar size="40" color="#061D47" class="mr-3">
                        <span class="white--text" style="font-size: 16px;">{{ getInitials(rec.name) }}</span>
                      </v-avatar>
                      <div>
                        <div style="font-weight: 600; font-size: 15px; color: #333;">{{ rec.name }}</div>
                        <div style="font-size: 12px; color: #888; text-transform: capitalize;">{{ rec.category }}</div>
                      </div>
                      <v-spacer></v-spacer>
                      <v-chip
                        small
                        :color="statusChipColor(rec.status)"
                        :text-color="rec.status === 'pending' ? '#333' : 'white'"
                        style="justify-content: center; text-transform: capitalize;"
                      >{{ rec.status }}</v-chip>
                    </div>

                    <div style="font-size: 13px; color: #555; margin-bottom: 8px;">
                      <v-icon small class="mr-1">phone</v-icon>
                      <a :href="'tel:' + rec.phone_number" style="color: #29ABE2;">{{ rec.phone_number }}</a>
                    </div>

                    <div v-if="rec.note" style="font-size: 12px; color: #666; background: #f8f9fa; padding: 8px 12px; border-radius: 6px; margin-bottom: 8px;">
                      "{{ rec.note }}"
                    </div>

                    <div style="font-size: 11px; color: #aaa;">
                      <template v-if="rec.origin === 'signup'">
                        <v-chip x-small color="#EEF4FF" text-color="#2F6FED" class="mr-1" style="font-weight: 600;">Self sign-up</v-chip>
                        Requested to join your team
                      </template>
                      <template v-else>
                        Recommended by <strong>{{ rec.recommended_by_name }}</strong>
                        <span v-if="rec.recommended_by_role">({{ rec.recommended_by_role }})</span>
                      </template>
                      <span v-if="rec.created_at"> &middot; {{ formatDate(rec.created_at) }}</span>
                    </div>
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions v-if="rec.status === 'pending'" class="d-flex justify-space-between px-3 py-2">
                    <div>
                      <v-btn x-small text color="#29ABE2" :href="'tel:' + rec.phone_number" class="mr-1">
                        <v-icon x-small left>phone</v-icon> Call
                      </v-btn>
                      <v-btn x-small text color="#29ABE2" :href="'sms:' + rec.phone_number">
                        <v-icon x-small left>message</v-icon> Text
                      </v-btn>
                    </div>
                    <div>
                      <v-btn x-small color="#4CAF50" dark @click="approve(rec)" :loading="approvingId === rec.id" class="mr-1">
                        Approve
                      </v-btn>
                      <v-btn x-small outlined color="#F44336" @click="decline(rec)" :loading="decliningId === rec.id">
                        Decline
                      </v-btn>
                    </div>
                  </v-card-actions>

                  <v-card-actions v-else-if="rec.status === 'approved'">
                    <span style="font-size: 11px; color: #4CAF50;">
                      <v-icon small color="#4CAF50" class="mr-1">check_circle</v-icon>
                      Approved &amp; invite sent
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn x-small outlined color="#F44336" @click="decline(rec)" :loading="decliningId === rec.id">
                      Decline
                    </v-btn>
                  </v-card-actions>

                  <v-card-actions v-else-if="rec.status === 'declined'">
                    <span style="font-size: 11px; color: #F44336;">
                      <v-icon small color="#F44336" class="mr-1">cancel</v-icon>
                      Declined
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn x-small color="#4CAF50" dark @click="approve(rec)" :loading="approvingId === rec.id">
                      Approve
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import axios from "axios";
import { auth } from "@/config/firebase";
import config from "@/config/constants.js";
import { mapState } from "vuex";

// Send both the Firebase ID token and the legacy Kiey JWT so /signup/review
// accepts the call regardless of which auth path the current session has.
async function buildSignupAuthHeaders() {
  const headers = {};
  try {
    const fbUser = auth.currentUser;
    if (fbUser) headers["X-Firebase-Token"] = await fbUser.getIdToken();
  } catch (e) {
    console.warn("[Referrals] failed to get Firebase ID token:", e && e.message);
  }
  const legacyToken = localStorage.getItem("auth_token")
    || (axios.defaults.headers && axios.defaults.headers.common && axios.defaults.headers.common["Authorization"])
    || "";
  if (legacyToken) headers["Authorization"] = legacyToken;
  return headers;
}

export default {
  name: "VendorRecommendations",
  data() {
    return {
      statusFilter: 'all',
      approvingId: null,
      decliningId: null,
    };
  },
  computed: {
    ...mapState({
      status: state => state.Referral_engine.status,
      recommendations: state => state.Referral_engine.recommendations,
    }),
    loading() {
      return this.status === 'loading';
    },
  },
  watch: {
    statusFilter() {
      this.fetchData();
    },
  },
  created() {
    this.$store.dispatch("Banner/show");
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.$store.dispatch('Referral_engine/fetchRecommendations', this.statusFilter);
    },
    async approve(rec) {
      const id = typeof rec === 'string' ? rec : rec.id;
      this.approvingId = id;
      if (rec && rec.origin === 'signup') {
        await this.reviewSignup(rec, 'approve');
      } else {
        await this.$store.dispatch('Referral_engine/approveRecommendation', id);
      }
      this.approvingId = null;
    },
    async decline(rec) {
      const id = typeof rec === 'string' ? rec : rec.id;
      this.decliningId = id;
      if (rec && rec.origin === 'signup') {
        await this.reviewSignup(rec, 'decline');
      } else {
        await this.$store.dispatch('Referral_engine/declineRecommendation', id);
      }
      this.decliningId = null;
    },
    // Signup-origin referrals approve/decline through the signup review
    // endpoint (not vendor/recommendations/:id/approve), because the source
    // of truth is the signup_requests doc.
    async reviewSignup(rec, action) {
      try {
        const headers = await buildSignupAuthHeaders();
        const { data } = await axios.post(config.api_url + 'signup/review', {
          request_id: rec.signup_request_id,
          action,
        }, { headers });
        if (data && data.code === 'SUCCESS') {
          await this.fetchData();
        }
      } catch (err) {
        console.error('[Referrals] signup review failed:', err);
      }
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/);
      return parts.map(p => p[0]).slice(0, 2).join('').toUpperCase();
    },
    statusChipColor(status) {
      switch (status) {
        case 'pending': return '#FFF3E0';
        case 'approved': return '#4CAF50';
        case 'declined': return '#F44336';
        default: return '#E0E0E0';
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      // Firestore timestamps have _seconds
      const date = timestamp._seconds
        ? new Date(timestamp._seconds * 1000)
        : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.rec-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 16px;
  padding: 8px 0;
}

.rec-card {
  border-radius: 8px !important;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  &--approved {
    border-left: 3px solid #4CAF50 !important;
  }

  &--declined {
    border-left: 3px solid #F44336 !important;
    opacity: 0.7;
  }
}
</style>
